import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { SendIconBlue } from 'pages/ListerVehicleUploadV2/icons/send';

export interface NoteItem {
  id: number;
  message: string;
  created_at: string;
  sender: { id: number; role: string; name: string };
}

const Block = styled.div`
  background: #fff;
  border-radius: 30px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-height: 0;
  overflow: hidden;

  @media (max-width: 576px) {
    max-height: 280px;
  }
`;

const BlockTitle = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #212121;
  flex-shrink: 0;
`;

const NotesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`;

const NoteRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const NoteHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const NoteBody = styled.div<{ align: 'left' | 'right' }>`
  display: flex;
  flex-direction: ${({ align }) => (align === 'right' ? 'row-reverse' : 'row')};
  align-items: flex-start;
  gap: 8px;
`;

const NoteAvatar = styled.div`
  width: 30px;
  height: 30px;
  min-width: 30px;
  border-radius: 50%;
  background: #3994de;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
`;

const NoteSenderName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #000;
`;

const NoteDate = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #72777a;
`;

const NoteBubble = styled.div<{ align: 'left' | 'right' }>`
  background: rgba(86, 111, 141, 0.1);
  border-radius: 24px;
  border-top-left-radius: ${({ align }) => (align === 'left' ? '0' : '24px')};
  border-top-right-radius: ${({ align }) => (align === 'right' ? '0' : '24px')};
  padding: 10px 14px;
`;

const NoteText = styled.p`
  font-size: 14px;
  font-weight: 400;
  color: #000;
  line-height: 18px;
  margin: 0;
`;

const InputRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
`;

const MessageInput = styled.input`
  flex: 1;
  padding: 10px 14px;
  background: #fafafa;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  font-size: 14px;
  outline: none;
  &:focus {
    border-color: #005a96;
  }
`;

const SendButton = styled.button`
  width: 34px;
  height: 34px;
  min-width: 34px;
  background: transparent;
  border: none;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
`;

const getInitial = (name: string) => name?.charAt(0)?.toUpperCase() || '?';

interface VehicleNotesProps {
  notesList: NoteItem[];
  currentRole: string;
  onSend: (message: string) => Promise<void>;
  isSending?: boolean;
  canSend?: boolean;
}

const VehicleNotes = ({ notesList, currentRole, onSend, isSending = false, canSend = true }: VehicleNotesProps) => {
  const [message, setMessage] = useState('');
  const notesListRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    if (notesListRef.current) {
      notesListRef.current.scrollTop = notesListRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    const id = setTimeout(scrollToBottom, 0);
    return () => clearTimeout(id);
  }, [notesList.length]);

  const handleSend = async () => {
    if (!message.trim() || isSending || !canSend) return;
    try {
      await onSend(message.trim());
      setMessage('');
      setTimeout(scrollToBottom, 0);
    } catch {
      // parent handles error display
    }
  };

  const setMobileNavHidden = (hidden: boolean) => {
    const nav = document.getElementById('mobile-nav');
    if (!nav) return;
    nav.classList.toggle('hidden', hidden);
    nav.classList.toggle('visible', !hidden);
  };

  const handleInputFocus = () => {
    setMobileNavHidden(true);
    // Wait for the mobile keyboard to finish opening before repositioning.
    setTimeout(() => {
      inputRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      scrollToBottom();
    }, 300);
  };

  const handleInputBlur = () => {
    setMobileNavHidden(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSend();
  };

  return (
    <Block>
      <BlockTitle>Notes</BlockTitle>
      <NotesList ref={notesListRef}>
        {notesList.map(note => {
          const align: 'left' | 'right' = note.sender.role === currentRole ? 'right' : 'left';
          const displayName = note.sender.role === 'admin' ? 'AutoAxess' : note.sender.name;
          const initial = getInitial(displayName);
          const formattedDate = note.created_at ? moment(note.created_at).format('ddd MM.DD.YY h:mm A') : '';
          return (
            <NoteRow key={note.id}>
              <NoteHeader>
                <NoteSenderName>{displayName}</NoteSenderName>
                <NoteDate>{formattedDate}</NoteDate>
              </NoteHeader>
              <NoteBody align={align}>
                <NoteAvatar>{initial}</NoteAvatar>
                <NoteBubble align={align}>
                  <NoteText>{note.message}</NoteText>
                </NoteBubble>
              </NoteBody>
            </NoteRow>
          );
        })}
      </NotesList>
      <InputRow>
        <MessageInput
          ref={inputRef}
          placeholder="Write a message..."
          value={message}
          onChange={e => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          disabled={isSending}
        />
        <SendButton onClick={handleSend} disabled={isSending || !message.trim() || !canSend}>
          <SendIconBlue />
        </SendButton>
      </InputRow>
    </Block>
  );
};

export default VehicleNotes;
